/**
 * MOBILE HERO — SECTION 5 · "Chaos → ein System" scroll stage (design map §5 ·
 * prototype lines 2046–2099).
 *
 * The centered serif heading ("Acht Uhr. Schon zwölf Programme offen." — `zwölf`
 * is the verdigris `<em>`) + subhead, then the SAME scroll-driven animation as
 * desktop ({@link ScrollStageCanvas}): the scattered fake windows fly into the
 * center anchor and resolve into the hub-and-spoke diagram. The canvas is a
 * fixed 1080px composition scaled down by {@link ResponsiveScale} to fit the
 * phone width (the prototype's `zoom:0.338`), and given `startOffset`/`span`
 * tuned for the short scaled stage so the animation actually plays out on screen.
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { ResponsiveScale } from "./hero-mobile-scale";
import { H, MOBILE_HERO_BAND_GUTTER, MOBILE_HERO_GUTTER } from "./hero-tokens";
import { ScrollStageCanvas } from "./sections/scroll-stage";

export function HeroMobileStage(): ReactNode {
  const t = useTranslations("marketing.heroMobileStage");
  return (
    <div style={{ position: "relative", margin: "60px auto 0" }}>
      <div
        style={{
          textAlign: "center",
          margin: "0 auto",
          padding: `0 ${MOBILE_HERO_GUTTER}px`,
        }}
      >
        <h2
          style={{
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: 30,
            lineHeight: 1.06,
            letterSpacing: "-0.018em",
            color: H.ink,
            margin: 0,
          }}
        >
          {t("achtUhrSchon")}{" "}
          <em style={{ fontStyle: "italic", color: H.accent }}>
            {t("zwoelf")}
          </em>{" "}
          {t("programmeOffen")}
        </h2>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.5,
            color: H.inkMute,
            margin: "14px auto 0",
          }}
        >
          {t("jederVorgangVerteiltMehrere")}
        </p>
      </div>

      {/* Same animated stage as desktop, scaled to the phone width. Center-anchored
          timing keeps the chaos windows visible at rest while the short scaled
          stage is in view and resolves them over a generous scroll range as it
          moves up (start 0.8 -> end -0.2 ≈ a half-viewport of flight; a narrow
          range made it snap through in ~3cm). headlineScale brings the scaled-down
          "Mach daraus zwei." back up to the native intro-heading size. shadowPad /
          shadowPadX reserve room inside ResponsiveScale's clip box so card
          box-shadows are not sheared off at the edges. */}
      {/* Full-bleed scaled stage; shadowPadX keeps card shadows inside the clip. */}
      <div style={{ margin: "20px 0 0" }}>
        <ResponsiveScale
          baseWidth={1080}
          shadowPad={26}
          shadowPadX={MOBILE_HERO_BAND_GUTTER}
        >
          <ScrollStageCanvas
            timing={{ mode: "center", start: 0.8, end: -0.2 }}
            headlineScale={1.92}
            compactDiagramShadow
            resolutionBlockOffsetY={72}
          />
        </ResponsiveScale>
      </div>
    </div>
  );
}
